import { fetchWithTokenRefresh } from '../utils/apiUtils';
import { normalizeRatingHistory } from '../utils/ratingHistory';

// BE-20261006-rating-v2.md 계약. 레거시 요약·순위 API로 대체하지 않는다.
const invalidResponse = () => new Error('레이팅 응답 형식을 확인할 수 없습니다.');
const isRating = (item) => item && Number.isInteger(item.student_id) && item.student_id > 0
  && Number.isInteger(item.rating) && Number.isInteger(item.exam_count) && item.exam_count >= 0;

const summaryOf = ({ student_id, rating, exam_count }) => ({ student_id, rating, exam_count });

export const RATING_BATCH_SIZE = 200;

// BE-20261031-rating-batch.md: 차단·비담당 학생도 포함하고, 없는 학생은 not_found로 구분한다.
export async function fetchStudentRatings(studentIds, getAccessToken, { signal } = {}) {
  if (!Array.isArray(studentIds) || !studentIds.every((id) => Number.isInteger(id) && id > 0)) {
    throw new Error('학생 번호를 확인해주세요.');
  }
  const ids = [...new Set(studentIds)];
  if (ids.length === 0 || ids.length > RATING_BATCH_SIZE) throw new Error('학생은 1~200명까지 조회할 수 있습니다.');
  const result = await fetchWithTokenRefresh('/api/v2/ratings/students', {
    method: 'POST', body: JSON.stringify({ student_ids: ids }), signal,
  }, getAccessToken);
  if (result == null) return null;
  const data = result.data;
  if (!data || !Array.isArray(data.items) || !Array.isArray(data.not_found)
      || !data.items.every(isRating)) throw invalidResponse();
  const remaining = new Set(ids);
  const byId = {};
  for (const item of data.items) {
    if (!remaining.delete(item.student_id)) throw invalidResponse();
    byId[item.student_id] = summaryOf(item);
  }
  for (const id of data.not_found) {
    if (!remaining.delete(id)) throw invalidResponse();
  }
  // 응답 누락을 기록 없음(1000점)으로 해석하지 않는다.
  if (remaining.size > 0) throw invalidResponse();
  return { byId, notFound: data.not_found };
}

export async function fetchLatestRatings(getAccessToken, { signal } = {}) {
  const result = await fetchWithTokenRefresh('/api/v2/ratings/latest', { signal }, getAccessToken);
  if (result == null) return null; // 공통 유틸이 로그인 화면으로 이동한다.
  const data = result.data;
  if (!data || !['all', 'mine'].includes(data.scope) || !Array.isArray(data.items)
      || !Number.isInteger(data.total) || data.total !== data.items.length
      || !data.items.every((item) => isRating(item) && item.exam_count > 0)) {
    throw invalidResponse();
  }
  const byId = {};
  for (const item of data.items) {
    if (byId[item.student_id]) throw invalidResponse();
    byId[item.student_id] = summaryOf(item);
  }
  return byId;
}

async function fetchStudentRatingData(studentId, getAccessToken, { signal } = {}) {
  const id = Number(studentId);
  if (!Number.isInteger(id) || id <= 0) throw new Error('학생 번호를 확인해주세요.');
  const result = await fetchWithTokenRefresh(
    `/api/v2/ratings/students/${id}?problems=false`, { signal }, getAccessToken,
  );
  if (result == null) return null;
  if (!isRating(result.data) || result.data.student_id !== id) throw invalidResponse();
  return result.data;
}

export async function fetchStudentRating(studentId, getAccessToken, options) {
  const data = await fetchStudentRatingData(studentId, getAccessToken, options);
  // 테이블에서는 시험별 시퀀스를 보관하지 않는다.
  return data == null ? null : summaryOf(data);
}

export async function fetchStudentRatingHistory(studentId, getAccessToken, options) {
  const data = await fetchStudentRatingData(studentId, getAccessToken, options);
  return data == null ? null : normalizeRatingHistory(data, studentId);
}
